// Phase-102 side-task · "See where PickNGo currently operates" modal.
// Lists every state with at least one active hub.
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { MapPin, Loader2 } from "lucide-react";
import api from "../services/api";

export default function ServingStatesModal({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const { data } = await api.get("/serviceability/v2/states-served");
        if (!cancelled) setRows(data?.states || []);
      } catch (_e) {
        if (!cancelled) setError("Couldn't load — try again");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="serving-states-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5 text-orange-500" />
            Where PickNGo operates
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            States with at least one active hub. We&apos;re expanding fast — join the waitlist for the rest.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 flex items-center justify-center text-sm text-slate-500"
               data-testid="serving-states-loading">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        )}

        {error && !loading && (
          <p className="py-4 text-sm text-rose-600" data-testid="serving-states-error">{error}</p>
        )}

        {!loading && !error && (
          <div className="py-2 flex flex-wrap gap-2 max-h-[40vh] overflow-y-auto"
               data-testid="serving-states-pills">
            {rows.length === 0 && (
              <p className="text-sm text-slate-500">No active states yet — check back soon.</p>
            )}
            {rows.map((r) => (
              <span
                key={r.state}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-sm"
                data-testid={`serving-state-${r.state}`}
              >
                <MapPin className="h-3.5 w-3.5 text-emerald-600" />
                <span className="font-medium text-emerald-900">{r.state}</span>
                <span className="text-xs text-emerald-700/80">· {r.active_hub_count} hub{r.active_hub_count > 1 ? "s" : ""}</span>
              </span>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
